import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { fetchRmsConfigMeta, fetchRmsOutputs } from "./rmsApi";
import {
  RmsMarketPriceReferenceBuilder,
  RmsMarketPriceReferenceLibrary,
} from "./RmsMarketPriceReferencesView";
import {
  formatCurrency,
  formatDateLabel,
  formatNumber,
  safeArray,
  toSortedUniqueDates,
} from "./rmsShared";
import { createRmsTranslator, formatRmsCount, getRmsLocale } from "./rmsPmsI18n";

const ROW_LIMIT = 20000;
const DEFAULT_VISIBLE_DAYS = 10;

function getTodayIso() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function cleanPlaceholder(value) {
  return value === "\u2014" ? "-" : value;
}

function safeFormatCurrency(value, locale, currency) {
  return cleanPlaceholder(formatCurrency(value, locale, currency));
}

function safeFormatDateLabel(value, locale) {
  return cleanPlaceholder(formatDateLabel(value, locale));
}

function formatCount(value, locale) {
  return formatRmsCount(value, locale);
}

function formatCollectedAt(value, locale) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale || "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildRatePlanLabel(entry, t) {
  const code = entry?.rate_plan_code;
  const ratePlanText = String(entry?.rate_plan || "").trim() || t("(none)");
  if (code == null || String(code).trim() === "") return ratePlanText;
  return `${code} - ${ratePlanText}`;
}

function getMealPlanKey(entry, t) {
  const text = String(entry?.meal_plan || "").trim();
  return text || t("(none)");
}

function getRatePlanKey(entry) {
  const code = entry?.rate_plan_code;
  if (code != null && String(code).trim() !== "") {
    return `code:${String(code)}`;
  }

  const text = String(entry?.rate_plan || "").trim();
  return text ? `text:${text}` : "text:(none)";
}

function buildMealPlanOptions(rows, t) {
  const counts = new Map();

  safeArray(rows).forEach((row) => {
    const key = getMealPlanKey(row, t);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: value,
      count,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

function buildRatePlanOptions(rows, t) {
  const entries = new Map();

  safeArray(rows).forEach((row) => {
    const value = getRatePlanKey(row);
    const count = entries.get(value)?.count || 0;
    entries.set(value, {
      value,
      label: buildRatePlanLabel(row, t),
      count: count + 1,
    });
  });

  return Array.from(entries.values()).sort((left, right) => left.label.localeCompare(right.label));
}

function buildStandardSummary(mappings, t) {
  return safeArray(mappings)
    .map((mapping) => {
      const label =
        mapping.standard_room_type_code || mapping.standard_room_type_name || t("Unmapped");
      const pax = mapping.max_occupancy ? `${mapping.max_occupancy} pax` : t("pax not set");
      return `${label} / ${pax}`;
    })
    .join(", ");
}

function buildOwnCategorySummary(categories) {
  return safeArray(categories)
    .map((item) => item.room_category_name)
    .filter(Boolean)
    .join(", ");
}

function getNightlyPriceLabel(entry, t) {
  const details = entry?.nightly_price_details;
  if (details?.estimated || details?.method === "los2_smoothed") return t("Estimated / night");
  if (details?.method === "los1_reconciled") return t("Verified with 1-night prices");
  if (details?.method === "los1_observed" || Number(entry?.los) === 1) return t("Observed / night");
  return t("Price / night");
}

function buildEntryTitle(entry, t, locale) {
  if (!entry) return "";

  const details = entry.nightly_price_details || {};
  const standardSummary = buildStandardSummary(entry.standard_mappings, t) || t("Not mapped yet");
  const ownCategories =
    buildOwnCategorySummary(entry.comparable_room_categories) ||
    t("No own room category linked yet");

  return [
    `${t("Price shown")}: ${safeFormatCurrency(
      entry.nightly_price,
      locale,
      entry.currency
    )}`,
    getNightlyPriceLabel(entry, t),
    details.method === "los2_smoothed"
      ? t("Estimate from the average nightly prices of 2-night stays that include this date.")
      : null,
    details.method === "los1_reconciled"
      ? t("Nightly price verified using 1-night quotes and a matching 2-night stay.")
      : null,
    details.sample_count != null ? t("{count} stays used", { count: details.sample_count }) : null,
    details.component_min != null && details.component_max != null
      ? `${t("Average nightly price range")}: ${safeFormatCurrency(details.component_min, locale, entry.currency)} – ${safeFormatCurrency(details.component_max, locale, entry.currency)}`
      : null,
    entry.check_in && entry.check_out
      ? `${t("Source stay")}: ${safeFormatDateLabel(entry.check_in, locale)} – ${safeFormatDateLabel(entry.check_out, locale)}`
      : null,
    details.stay_total != null
      ? `${t("Source stay total")}: ${safeFormatCurrency(details.stay_total, locale, entry.currency)}`
      : null,
    details.stay_average != null
      ? `${t("Source stay average / night")}: ${safeFormatCurrency(details.stay_average, locale, entry.currency)}`
      : null,
    `${t("Meal plan")}: ${getMealPlanKey(entry, t)}`,
    `${t("Rate conditions")}: ${buildRatePlanLabel(entry, t)}`,
    entry.is_promo ? `${t("Promotion")}: ${entry.promo_name || t("Yes")}` : null,
    entry.pay_plan ? `${t("Payment")}: ${entry.pay_plan}` : null,
    entry.tax_type ? `${t("Taxes")}: ${entry.tax_type}` : null,
    `${t("Occupancy")}: ${entry.occupancy ?? "-"}`,
    `${t("LOS")}: ${entry.los ?? "-"}`,
    `${t("Standard type")}: ${standardSummary}`,
    `${t("My room categories")}: ${ownCategories}`,
    entry.closed ? `${t("Closed")}: ${t("Yes").toLowerCase()}` : `${t("Closed")}: ${t("No").toLowerCase()}`,
    entry.source_url ? t("Click to open source") : null,
  ]
    .filter(Boolean)
    .join("\n");
}

function chooseLatestEntry(currentEntry, nextEntry) {
  if (!currentEntry) return nextEntry;
  if (!nextEntry) return currentEntry;

  const currentCollected = currentEntry.dt_collected ? new Date(currentEntry.dt_collected).getTime() : 0;
  const nextCollected = nextEntry.dt_collected ? new Date(nextEntry.dt_collected).getTime() : 0;
  return nextCollected >= currentCollected ? nextEntry : currentEntry;
}

function normalizeOfferPart(value) {
  return String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function buildOfferKey(row) {
  if (row?.offer_identity) return `${row.offer_identity}::los:${row.los ?? ""}`;
  return [
    row?.hotel_code ?? row?.competitor_name ?? "hotel",
    row?.website_code ?? "source",
    normalizeOfferPart(row?.room_type_norm || row?.room_type),
    row?.occupancy ?? "occupancy",
    row?.guests ?? "guests",
    row?.los ?? "los",
    normalizeOfferPart(row?.meal_plan),
    row?.rate_plan_code ?? normalizeOfferPart(row?.rate_plan),
    normalizeOfferPart(row?.rate_plan),
    row?.meal_plan_code ?? "meal_plan_code",
    row?.is_promo ?? "promo:(missing)",
    normalizeOfferPart(row?.promo_name),
    row?.discount ?? "discount:(missing)",
    normalizeOfferPart(row?.pay_plan),
    row?.tax_status ?? "tax_status:(missing)",
    normalizeOfferPart(row?.tax_type),
    row?.tax_amount ?? "tax_amount:(missing)",
    row?.pos ?? "pos:(missing)",
    String(row?.currency || "").trim().toUpperCase() || "currency:(missing)",
  ].join("::");
}

function buildOfferMeta(line, t) {
  return [
    line.source_name || (line.website_code ? `${t("Source")} ${line.website_code}` : t("Unknown source")),
    line.occupancy ? `${line.occupancy} ${t("pax")}` : t("pax n/a"),
    line.los ? `${line.los}N` : t("LOS n/a"),
    line.meal_plan || t("No meal plan"),
    line.rate_plan || t("No rate conditions"),
    line.is_promo ? line.promo_name || t("Promotion") : null,
    line.pay_plan || null,
    line.tax_type || null,
  ].filter(Boolean).join(" / ");
}

function MappingCell({ mappings, t }) {
  const items = safeArray(mappings);
  if (!items.length) {
    return <span className="rmo-chip rmo-chip--muted">{t("Not mapped")}</span>;
  }

  return (
    <div className="rmo-chipRow" title={buildStandardSummary(items, t)}>
      {items.map((mapping) => {
        const label =
          mapping.standard_room_type_code || mapping.standard_room_type_name || t("Standard");
        const pax = mapping.max_occupancy
          ? `${mapping.max_occupancy} ${t("pax")}`
          : t("pax");
        return (
          <span
            key={`${mapping.standard_room_type_id}-${mapping.max_occupancy}`}
            className="rmo-chip rmo-chip--standard"
          >
            {label} / {pax}
          </span>
        );
      })}
    </div>
  );
}

function CategoriesCell({ categories, t }) {
  const items = safeArray(categories);
  if (!items.length) {
    return <span className="rmo-muted">{t("Not linked yet")}</span>;
  }

  return (
    <div className="rmo-chipRow" title={buildOwnCategorySummary(items)}>
      {items.map((item) => (
        <span key={item.room_category_id} className="rmo-chip rmo-chip--category">
          {item.room_category_name}
        </span>
      ))}
    </div>
  );
}

function MarketPriceCell({ entry, t, locale }) {
  if (!entry) {
    return (
      <div className="rmo-priceTile rmo-priceTile--empty">
        <div className="rmo-priceValue">-</div>
      </div>
    );
  }

  const occupancyLabel = entry.occupancy ? `${entry.occupancy} ${t("pax")}` : t("pax n/a");
  const losLabel = entry.los ? `${entry.los}N` : t("LOS n/a");
  const tileClassName = `rmo-priceTile${entry.closed ? " isClosed" : ""}`;

  const content = (
    <div className={tileClassName}>
      {entry.closed ? <span className="rmo-priceState">{t("Closed")}</span> : null}
      <div className="rmo-priceValue">
        {safeFormatCurrency(entry.nightly_price, locale, entry.currency)}
      </div>
      <div className="rmo-priceMeta">
        {occupancyLabel} / {losLabel}
      </div>
      {!entry.closed && entry.nightly_price != null ? (
        <div className="rmo-priceMeta" data-price-method={entry.nightly_price_details?.method || "unknown"}>
          {getNightlyPriceLabel(entry, t)}
        </div>
      ) : null}
    </div>
  );

  if (!entry.source_url) {
    return <div title={buildEntryTitle(entry, t, locale)}>{content}</div>;
  }

  return (
    <button
      type="button"
      className="rmo-priceTileBtn"
      onClick={() => window.open(entry.source_url, "_blank", "noopener,noreferrer")}
      title={buildEntryTitle(entry, t, locale)}
    >
      {content}
    </button>
  );
}

function MultiSelectFilterModal({
  isOpen,
  title,
  helpText,
  options,
  selectedValues,
  onApply,
  onClose,
  t,
  locale,
}) {
  const [search, setSearch] = useState("");
  const [localValues, setLocalValues] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setLocalValues(Array.isArray(selectedValues) ? [...selectedValues] : []);
  }, [isOpen, selectedValues]);

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return safeArray(options);

    return safeArray(options).filter((option) =>
      String(option.label || "").toLowerCase().includes(needle)
    );
  }, [options, search]);

  if (!isOpen) return null;

  return (
    <div className="rmo-modalBackdrop" onClick={onClose}>
      <div className="rmo-modal rmo-modal--narrow" onClick={(event) => event.stopPropagation()}>
        <div className="rmo-modalHead">
          <div>
            <div className="rmo-headTitle" style={{ fontSize: "1.02rem" }}>
              {title}
            </div>
            <div className="rmo-headSubtitle">{helpText}</div>
          </div>

          <button type="button" className="rmo-btn" onClick={onClose}>
            {t("Close")}
          </button>
        </div>

        <div className="rmo-modalBody">
          <input
            className="rmo-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Search options...")}
            style={{ width: "100%", marginBottom: 14 }}
          />

          <div className="rmo-filterOptionList">
            {visibleOptions.map((option) => {
              const checked = localValues.includes(option.value);
              return (
                <label key={option.value} className="rmo-filterOptionRow">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setLocalValues((current) => {
                        if (current.includes(option.value)) {
                          return current.filter((value) => value !== option.value);
                        }
                        return [...current, option.value];
                      });
                    }}
                  />

                  <span className="rmo-filterOptionText">{option.label}</span>
                  <span className="rmo-filterOptionCount">{formatCount(option.count, locale)}</span>
                </label>
              );
            })}

            {!visibleOptions.length ? (
              <div className="rmo-empty" style={{ padding: 0 }}>
                {t("No options match this search.")}
              </div>
            ) : null}
          </div>
        </div>

        <div className="rmo-modalFoot">
          <button type="button" className="rmo-btn" onClick={() => setLocalValues([])}>
            {t("Clear")}
          </button>
          <button type="button" className="rmo-btn rmo-btn--accent" onClick={() => onApply(localValues)}>
            {t("Apply")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RmsMarketBenchmarkView({
  businessUnitId,
  canApplyMarketPrices = false,
}) {
  const { languageId, language } = usePmsTheme();
  const t = useMemo(() => createRmsTranslator(languageId), [languageId]);
  const locale = getRmsLocale(languageId, language);
  const dateLocale = languageId === "en" ? "en-GB" : locale || "es-ES";
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [roomCategoryId, setRoomCategoryId] = useState("");
  const [competitorFilter, setCompetitorFilter] = useState("");
  const [losFilter, setLosFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedMealPlans, setSelectedMealPlans] = useState([]);
  const [selectedRatePlans, setSelectedRatePlans] = useState([]);
  const [filterModal, setFilterModal] = useState("");
  const [workspaceMode, setWorkspaceMode] = useState("observed");
  const [selectedSeriesKeys, setSelectedSeriesKeys] = useState([]);
  const [referenceRefreshVersion, setReferenceRefreshVersion] = useState(0);

  const defaultDateRange = useMemo(() => {
    const today = getTodayIso();
    const start = meta?.defaults?.market_date_from || today;
    const end = meta?.defaults?.market_date_to || addDaysIso(start, DEFAULT_VISIBLE_DAYS - 1);
    return { start, end };
  }, [meta]);

  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      try {
        const payload = await fetchRmsConfigMeta();
        if (cancelled) return;

        const start = payload?.defaults?.market_date_from || getTodayIso();
        const end = payload?.defaults?.market_date_to || addDaysIso(start, DEFAULT_VISIBLE_DAYS - 1);

        setMeta(payload);
        setDateFrom((current) => current || start);
        setDateTo((current) => current || end);
      } catch (nextError) {
        if (!cancelled) {
          setError(
            nextError?.response?.data?.error ||
              nextError?.message ||
              t("Could not load RMS meta.")
          );
        }
      }
    };

    loadMeta();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const loadBenchmark = useCallback(async () => {
    if (!businessUnitId) return;
    if (!meta && !dateFrom && !dateTo) return;

    const effectiveDateFrom = dateFrom || defaultDateRange.start;
    const effectiveDateTo = dateTo || defaultDateRange.end;

    if (!effectiveDateFrom || !effectiveDateTo) return;

    try {
      setLoading(true);
      setError("");
      const payload = await fetchRmsOutputs("market-benchmark", {
        business_unit_id: businessUnitId,
        room_category_id: roomCategoryId || undefined,
        date_from: effectiveDateFrom,
        date_to: effectiveDateTo,
        limit: ROW_LIMIT,
      });
      setData(payload);
    } catch (nextError) {
          setError(
            nextError?.response?.data?.error ||
              nextError?.message ||
              t("Could not load market benchmark.")
          );
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, dateFrom, dateTo, defaultDateRange.end, defaultDateRange.start, meta, roomCategoryId, t]);

  useEffect(() => {
    loadBenchmark();
  }, [loadBenchmark]);

  const competitorOptions = useMemo(() => {
    const names = new Set();
    safeArray(data?.rows).forEach((row) => {
      if (row?.competitor_name) names.add(row.competitor_name);
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [data]);

  const competitorScopedRows = useMemo(() => {
    if (!competitorFilter) return safeArray(data?.rows);
    return safeArray(data?.rows).filter((row) => row.competitor_name === competitorFilter);
  }, [competitorFilter, data]);

  const mealPlanOptions = useMemo(
    () => buildMealPlanOptions(competitorScopedRows, t),
    [competitorScopedRows, t]
  );

  const ratePlanOptions = useMemo(
    () => buildRatePlanOptions(competitorScopedRows, t),
    [competitorScopedRows, t]
  );

  useEffect(() => {
    const allowed = new Set(mealPlanOptions.map((option) => option.value));
    setSelectedMealPlans((current) => current.filter((value) => allowed.has(value)));
  }, [mealPlanOptions]);

  useEffect(() => {
    const allowed = new Set(ratePlanOptions.map((option) => option.value));
    setSelectedRatePlans((current) => current.filter((value) => allowed.has(value)));
  }, [ratePlanOptions]);

  const ratePlanLabelByValue = useMemo(() => {
    const labels = new Map();
    ratePlanOptions.forEach((option) => labels.set(option.value, option.label));
    return labels;
  }, [ratePlanOptions]);

  const filteredRows = useMemo(() => {
    return competitorScopedRows.filter((row) => {
      if (losFilter && Number(row.los) !== Number(losFilter)) return false;
      if (selectedMealPlans.length && !selectedMealPlans.includes(getMealPlanKey(row, t))) {
        return false;
      }

      if (selectedRatePlans.length && !selectedRatePlans.includes(getRatePlanKey(row))) {
        return false;
      }

      return true;
    });
  }, [competitorScopedRows, losFilter, selectedMealPlans, selectedRatePlans, t]);

  const groupedHotels = useMemo(() => {
    const hotelMap = new Map();

    filteredRows.forEach((row) => {
      const hotelKey = String(row.hotel_code || row.competitor_name || "hotel");
      if (!hotelMap.has(hotelKey)) {
        hotelMap.set(hotelKey, {
          hotel_code: row.hotel_code,
          competitor_name: row.competitor_name,
          competitor_weight: row.competitor_weight,
          linesMap: new Map(),
        });
      }

      const hotel = hotelMap.get(hotelKey);
      const offerKey = buildOfferKey(row);
      if (!hotel.linesMap.has(offerKey)) {
        hotel.linesMap.set(offerKey, {
          key: offerKey,
          room_type: row.room_type || "-",
          room_type_norm: row.room_type_norm || "",
          website_code: row.website_code ?? null,
          source_name: row.source_name || null,
          occupancy: row.occupancy ?? null,
          guests: row.guests ?? null,
          los: row.los ?? null,
          meal_plan: row.meal_plan || null,
          meal_plan_code: row.meal_plan_code ?? null,
          rate_plan: row.rate_plan || null,
          rate_plan_code: row.rate_plan_code ?? null,
          offer_identity: row.offer_identity ?? null,
          is_promo: row.is_promo ?? null,
          promo_name: row.promo_name ?? null,
          discount: row.discount ?? null,
          pay_plan: row.pay_plan ?? null,
          tax_status: row.tax_status ?? null,
          tax_type: row.tax_type ?? null,
          tax_amount: row.tax_amount ?? null,
          pos: row.pos ?? null,
          currency: String(row.currency || "").trim().toUpperCase() || null,
          standardMappingMap: new Map(),
          roomCategoryMap: new Map(),
          entriesByDate: {},
        });
      }

      const line = hotel.linesMap.get(offerKey);

      safeArray(row.standard_mappings).forEach((mapping) => {
        line.standardMappingMap.set(
          `${mapping.standard_room_type_id}-${mapping.max_occupancy}`,
          mapping
        );
      });

      safeArray(row.comparable_room_categories).forEach((item) => {
        line.roomCategoryMap.set(String(item.room_category_id), item);
      });

      line.entriesByDate[row.stay_date] = chooseLatestEntry(
        line.entriesByDate[row.stay_date],
        row
      );
    });

    return Array.from(hotelMap.values())
      .map((hotel) => {
        const lines = Array.from(hotel.linesMap.values())
          .map((line) => ({
            ...line,
            standardMappings: Array.from(line.standardMappingMap.values()).sort((left, right) => {
              const typeCompare = String(left.standard_room_type_code || "").localeCompare(
                String(right.standard_room_type_code || "")
              );
              if (typeCompare !== 0) return typeCompare;
              return Number(left.max_occupancy || 0) - Number(right.max_occupancy || 0);
            }),
            comparableRoomCategories: Array.from(line.roomCategoryMap.values()).sort((left, right) =>
              String(left.room_category_name || "").localeCompare(
                String(right.room_category_name || "")
              )
            ),
          }))
          .sort((left, right) => {
            const roomCompare = String(left.room_type || "").localeCompare(
              String(right.room_type || "")
            );
            if (roomCompare !== 0) return roomCompare;
            return buildOfferMeta(left, t).localeCompare(buildOfferMeta(right, t));
          });

        return {
          ...hotel,
          lines,
          mappedLinesCount: lines.filter((line) => line.standardMappings.length).length,
        };
      })
      .sort((left, right) =>
        String(left.competitor_name || "").localeCompare(String(right.competitor_name || ""))
      );
  }, [filteredRows, t]);

  const availableSeries = useMemo(
    () =>
      groupedHotels.flatMap((hotel) =>
        hotel.lines.map((line) => ({
          key: line.key,
          label: `${hotel.competitor_name || t("Competitor")} / ${line.room_type} / ${buildOfferMeta(line, t)}`,
          hotel_code: Number(hotel.hotel_code),
          hotel_name: hotel.competitor_name || null,
          website_code: line.website_code,
          source_name: line.source_name,
          room_type: line.room_type,
          room_type_norm: line.room_type_norm,
          occupancy: line.occupancy,
          guests: line.guests,
          los: line.los,
          meal_plan: line.meal_plan,
          meal_plan_code: line.meal_plan_code,
          rate_plan: line.rate_plan,
          rate_plan_code: line.rate_plan_code,
          offer_identity: line.offer_identity,
          is_promo: line.is_promo,
          promo_name: line.promo_name,
          discount: line.discount,
          pay_plan: line.pay_plan,
          tax_status: line.tax_status,
          tax_type: line.tax_type,
          tax_amount: line.tax_amount,
          pos: line.pos,
          currency: line.currency,
        }))
      ),
    [groupedHotels, t]
  );

  useEffect(() => {
    const availableKeys = new Set(availableSeries.map((series) => series.key));
    setSelectedSeriesKeys((current) => current.filter((key) => availableKeys.has(key)));
  }, [availableSeries]);

  const allDates = useMemo(() => toSortedUniqueDates(filteredRows, "stay_date"), [filteredRows]);

  const summaryCards = useMemo(() => {
    let roomLinesCount = 0;
    let mappedRoomLines = 0;
    let lastCollectedAt = null;

    groupedHotels.forEach((hotel) => {
      roomLinesCount += hotel.lines.length;
      mappedRoomLines += hotel.mappedLinesCount;
    });

    filteredRows.forEach((row) => {
      if (!row?.dt_collected) return;
      const iso = new Date(row.dt_collected).toISOString();
      if (!lastCollectedAt || iso > lastCollectedAt) {
        lastCollectedAt = iso;
      }
    });

    return [
      {
        label: t("Competitors"),
        value: formatCount(groupedHotels.length, locale),
        meta: t("Hotels visible with the current filters"),
      },
      {
        label: t("Exact offers"),
        value: formatCount(roomLinesCount, locale),
        meta: t("Separate source, room, occupancy, LOS and rate conditions"),
      },
      {
        label: t("Mapped offers"),
        value: formatCount(mappedRoomLines, locale),
        meta: t("Offers already linked to an RMS standard type"),
      },
      {
        label: t("Latest snapshot"),
        value: formatCollectedAt(lastCollectedAt || data?.summary?.latest_collected_at, locale),
        meta: t("Most recent market extraction shown here"),
      },
    ];
  }, [data, filteredRows, groupedHotels, locale, t]);

  const selectedDateFrom = dateFrom || defaultDateRange.start;
  const selectedDateTo = dateTo || defaultDateRange.end;

  const selectedFilterPills = useMemo(() => {
    return [
      ...selectedMealPlans.map((value) => ({
        kind: "meal",
        value,
        label: value,
      })),
      ...selectedRatePlans.map((value) => ({
        kind: "rate",
        value,
        label: ratePlanLabelByValue.get(value) || value,
      })),
    ];
  }, [ratePlanLabelByValue, selectedMealPlans, selectedRatePlans]);

  const workspaceNav = (
    <div className="rmo-marketWorkspaceNav" role="tablist" aria-label={t("Market pricing workspace")}>
      <button
        type="button"
        role="tab"
        aria-selected={workspaceMode === "observed"}
        className={workspaceMode === "observed" ? "isActive" : ""}
        onClick={() => setWorkspaceMode("observed")}
      >
        <span>{t("Observed prices")}</span>
        <small>{t("See raw competitor offers")}</small>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={workspaceMode === "reference"}
        className={workspaceMode === "reference" ? "isActive" : ""}
        onClick={() => setWorkspaceMode("reference")}
      >
        <span>{t("Create reference")}</span>
        <small>
          {selectedSeriesKeys.length
            ? t("{count} offers selected", { count: selectedSeriesKeys.length })
            : t("Select exact offers")}
        </small>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={workspaceMode === "saved"}
        className={workspaceMode === "saved" ? "isActive" : ""}
        onClick={() => setWorkspaceMode("saved")}
      >
        <span>{t("Saved references")}</span>
        <small>{t("Review or apply later")}</small>
      </button>
    </div>
  );

  if (workspaceMode === "reference") {
    return (
      <div className="rmo-sectionStack">
        {workspaceNav}
        <RmsMarketPriceReferenceBuilder
          businessUnitId={businessUnitId}
          availableSeries={availableSeries}
          selectedSeriesKeys={selectedSeriesKeys}
          onSelectedSeriesKeysChange={setSelectedSeriesKeys}
          dateFrom={selectedDateFrom}
          dateTo={selectedDateTo}
          canApplyMarketPrices={canApplyMarketPrices}
          t={t}
          locale={locale}
          onSaved={() => setReferenceRefreshVersion((current) => current + 1)}
        />
      </div>
    );
  }

  if (workspaceMode === "saved") {
    return (
      <div className="rmo-sectionStack">
        {workspaceNav}
        <RmsMarketPriceReferenceLibrary
          businessUnitId={businessUnitId}
          canApplyMarketPrices={canApplyMarketPrices}
          t={t}
          locale={locale}
          refreshVersion={referenceRefreshVersion}
        />
      </div>
    );
  }

  return (
    <div className="rmo-sectionStack">
      {workspaceNav}
      {error ? <div className="rmo-errorBox">{error}</div> : null}

      <div className="rmo-surface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Market Benchmark")}</h3>
            <div className="rmo-headSubtitle">
              {t("Raw competitor prices. Each row is one exact source, room, occupancy, LOS, meal plan and rate condition.")}
            </div>
          </div>
        </div>

        <div className="rmo-toolbar">
          <div className="rmo-toolbarGroup">
            <select
              className="rmo-select"
              value={roomCategoryId}
              onChange={(event) => setRoomCategoryId(event.target.value)}
            >
              <option value="">{t("All room categories")}</option>
              {safeArray(meta?.room_categories).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

            <select
              className="rmo-select"
              value={competitorFilter}
              onChange={(event) => setCompetitorFilter(event.target.value)}
            >
              <option value="">{t("All competitors")}</option>
              {competitorOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <select
              className="rmo-select"
              aria-label={t("Stay length")}
              value={losFilter}
              onChange={(event) => setLosFilter(event.target.value)}
            >
              <option value="">{t("All stay lengths")}</option>
              <option value="1">{t("1 night")} (LOS1)</option>
              <option value="2">{t("2 nights")} (LOS2)</option>
            </select>

            <button
              type="button"
              className={`rmo-filterTrigger${selectedMealPlans.length ? " isActive" : ""}`}
              onClick={() => setFilterModal("meal")}
            >
              {t("Meal plans")}
              <span className="rmo-filterTriggerCount">
                {selectedMealPlans.length || formatCount(mealPlanOptions.length, locale)}
              </span>
            </button>

            <button
              type="button"
              className={`rmo-filterTrigger${selectedRatePlans.length ? " isActive" : ""}`}
              onClick={() => setFilterModal("rate")}
            >
              {t("Rate conditions")}
              <span className="rmo-filterTriggerCount">
                {selectedRatePlans.length || formatCount(ratePlanOptions.length, locale)}
              </span>
            </button>
          </div>

          <div className="rmo-toolbarGroup">
            <input
              className="rmo-input"
              type="date"
              value={selectedDateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
            <input
              className="rmo-input"
              type="date"
              value={selectedDateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />

            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={loadBenchmark}
              disabled={loading}
            >
              {loading ? t("Refreshing...") : t("Refresh")}
            </button>
          </div>
        </div>

        <div className="rmo-filterSummary">
          <span className="rmo-filterSummaryText">
            {t("Showing {from} to {to}", {
              from: safeFormatDateLabel(selectedDateFrom, dateLocale),
              to: safeFormatDateLabel(selectedDateTo, dateLocale),
            })}
          </span>

          {selectedFilterPills.map((pill) => (
            <button
              key={`${pill.kind}-${pill.value}`}
              type="button"
              className="rmo-filterPill"
              onClick={() => {
                if (pill.kind === "meal") {
                  setSelectedMealPlans((current) => current.filter((value) => value !== pill.value));
                  return;
                }
                setSelectedRatePlans((current) => current.filter((value) => value !== pill.value));
              }}
            >
              <span>{pill.label}</span>
              <span className="rmo-filterPillClose">x</span>
            </button>
          ))}

          {selectedFilterPills.length ? (
            <button
              type="button"
              className="rmo-filterReset"
              onClick={() => {
                setSelectedMealPlans([]);
                setSelectedRatePlans([]);
              }}
            >
              {t("Clear filters")}
            </button>
          ) : null}
        </div>

        <div className="rmo-kpiGrid">
          {summaryCards.map((card) => (
            <div key={card.label} className="rmo-kpiCard">
              <div className="rmo-kpiLabel">{card.label}</div>
              <div className="rmo-kpiValue">{card.value}</div>
              <div className="rmo-kpiMeta">{card.meta}</div>
            </div>
          ))}
        </div>

        <div className={`rmo-marketSelectionBar${selectedSeriesKeys.length ? " hasSelection" : ""}`}>
          <div>
            <strong>
              {selectedSeriesKeys.length
                ? t("{count} exact offers selected", { count: selectedSeriesKeys.length })
                : t("Select the exact competitor offers you want to use")}
            </strong>
            <span>
              {selectedSeriesKeys.length
                ? t("These offers will be preserved individually in the market reference.")
                : t("Select an offer. Prices are shown per night; estimates are marked and explained in the price detail.")}
            </span>
          </div>
          <div className="rmo-inlineActions">
            {selectedSeriesKeys.length ? (
              <button
                type="button"
                className="rmo-btn"
                onClick={() => setSelectedSeriesKeys([])}
              >
                {t("Clear selection")}
              </button>
            ) : null}
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              disabled={!selectedSeriesKeys.length}
              onClick={() => setWorkspaceMode("reference")}
            >
              {t("Create reference with selection")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="rmo-empty">{t("Loading competitor prices...")}</div>
        ) : !groupedHotels.length || !allDates.length ? (
          <div className="rmo-empty">{t("No competitor prices match the current filters.")}</div>
        ) : (
          <div
            className="rmo-tableWrap rmo-marketTableWrap"
            style={{
              "--rmo-col-1": "280px",
              "--rmo-col-2": "180px",
              "--rmo-col-3": "220px",
            }}
          >
            <table className="rmo-table rmo-marketTable">
              <thead>
                <tr>
                  <th className="rmo-sticky s1">{t("Competitor offer")}</th>
                  <th className="rmo-sticky s2">{t("Standard type")}</th>
                  <th className="rmo-sticky s3">{t("My room categories")}</th>
                  {allDates.map((date) => (
                    <th key={date} className="rmo-marketDayHead">
                      <div className="rmo-dayHeaderMain">{safeFormatDateLabel(date, dateLocale)}</div>
                      <div className="rmo-dayHeaderSub">{date}</div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {groupedHotels.map((hotel) => (
                  <React.Fragment key={`hotel-${hotel.hotel_code || hotel.competitor_name}`}>
                    <tr className="rmo-hotelBandRow">
                      <td className="rmo-sticky s1 rmo-hotelBandName">
                        <div className="rmo-hotelBandTitle">{hotel.competitor_name || t("Competitor")}</div>
                        <div className="rmo-hotelBandSub">
                          {hotel.hotel_code
                            ? t("Code {code}", { code: hotel.hotel_code })
                            : t("Competitor hotel")}
                        </div>
                      </td>
                      <td colSpan={2 + allDates.length} className="rmo-hotelBandBarCell">
                        <div className="rmo-hotelBandBar">
                          {hotel.competitor_weight != null ? (
                            <span className="rmo-pill rmo-pill--soft">
                              {t("Weight")} {formatNumber(hotel.competitor_weight, 2, locale)}
                            </span>
                          ) : null}
                          <span className="rmo-pill rmo-pill--soft">
                            {t("{count} exact offers", { count: formatCount(hotel.lines.length, locale) })}
                          </span>
                          <span className="rmo-pill rmo-pill--soft">
                            {t("{count} mapped", {
                              count: formatCount(hotel.mappedLinesCount, locale),
                            })}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {hotel.lines.map((line) => {
                      const isSelected = selectedSeriesKeys.includes(line.key);
                      return (
                      <tr
                        key={line.key}
                        className={isSelected ? "rmo-marketOfferRow isSelected" : "rmo-marketOfferRow"}
                      >
                        <td className="rmo-sticky s1 rmo-marketStickyCell">
                          <label className="rmo-marketOfferSelector">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedSeriesKeys((current) =>
                                  current.includes(line.key)
                                    ? current.filter((key) => key !== line.key)
                                    : [...current, line.key]
                                );
                              }}
                            />
                            <span className="rmo-marketRoomCell">
                              <span className="rmo-marketRoomTitle">{line.room_type || "-"}</span>
                              <span className="rmo-marketRoomMeta">{buildOfferMeta(line, t)}</span>
                            </span>
                          </label>
                        </td>

                        <td className="rmo-sticky s2 rmo-marketStickyCell">
                          <MappingCell mappings={line.standardMappings} t={t} />
                        </td>

                        <td className="rmo-sticky s3 rmo-marketStickyCell">
                          <CategoriesCell categories={line.comparableRoomCategories} t={t} />
                        </td>

                        {allDates.map((date) => (
                          <td
                            key={[
                              hotel.hotel_code || hotel.competitor_name,
                              line.room_type_norm || line.room_type,
                              date,
                            ].join("::")}
                            className="rmo-marketPriceCell"
                          >
                            <MarketPriceCell entry={line.entriesByDate[date]} t={t} locale={locale} />
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MultiSelectFilterModal
        isOpen={filterModal === "meal"}
        title={t("Meal plans")}
        helpText={t("Choose which meal plans should stay visible in the benchmark.")}
        options={mealPlanOptions}
        selectedValues={selectedMealPlans}
        onApply={(values) => {
          setSelectedMealPlans(values);
          setFilterModal("");
        }}
        onClose={() => setFilterModal("")}
        t={t}
        locale={locale}
      />

      <MultiSelectFilterModal
        isOpen={filterModal === "rate"}
        title={t("Rate conditions")}
        helpText={t("Choose which rate plans or cancellation conditions should stay visible.")}
        options={ratePlanOptions}
        selectedValues={selectedRatePlans}
        onApply={(values) => {
          setSelectedRatePlans(values);
          setFilterModal("");
        }}
        onClose={() => setFilterModal("")}
        t={t}
        locale={locale}
      />
    </div>
  );
}
